import React, { useMemo } from 'react';
import type { TooltipProps } from 'recharts';
import { PCAReportCountField, PCAReportMetric } from 'c-sdk';
import { Box, Divider, Paper, Typography, useTheme } from '@mui/material';
import { formatNumber, formatNumberWhole, secondsToHms } from 'c-lib';
import { NumberFormat, NumberFormatWhole } from 'c-components';
import { ReportDateTimeframe } from 'c-reports/Types';
import { FieldReportType } from 'c-main/Components/Campaign/CampaignReports/types';
import { Translate } from 'c-translation';
import HoursMins from '../../HoursMins';

type Props = {
    tooltipProps: TooltipProps<any, any>;
    expectedLabel: string;
    percentageLabel: string;
    inScheduleLabel: string;
    outScheduleLabel: string;
    unbookedLabel: string;
    timeFrame: ReportDateTimeframe;
    type: FieldReportType;
    field: string;
    metric: PCAReportMetric;
    countFields: PCAReportCountField[];
    hideActualValue?: boolean;
    hidePercentage?: boolean;
    showPerformance?: boolean;
    performanceLabel?: string;
};

const CustomTooltip: React.FC<Props> = ({
    tooltipProps,
    expectedLabel,
    percentageLabel,
    inScheduleLabel,
    outScheduleLabel,
    unbookedLabel,
    type,
    timeFrame,
    field,
    metric,
    countFields,
    hideActualValue = false,
    hidePercentage = false,
    showPerformance = false,
    performanceLabel,
}) => {
    const theme = useTheme();
    const showCountFields = useMemo(() => {
        if (countFields.length === 0) {
            return false;
        }

        if (type === FieldReportType.OverTime && field != null) {
            return false;
        }

        return true;
    }, [countFields.length, field, type]);

    const performance = useMemo(() => {
        if (!showPerformance) return null;
        const payload = tooltipProps.payload ?? [];

        const rawValue = (entry: any): number | null => {
            const fullKey = `${entry?.dataKey}__full`;
            const raw =
                entry?.payload?.[fullKey] !== undefined
                    ? entry.payload[fullKey]
                    : entry?.payload?.[entry?.dataKey];
            const num = Number(raw);
            return Number.isFinite(num) ? num : null;
        };

        const primaryEntry = payload.find(p => p?.color === theme.palette.primary.main);
        const greyEntry = payload.find(p => p?.color === theme.palette.grey[500]);
        if (!primaryEntry || !greyEntry) return null;

        const primaryValue = rawValue(primaryEntry);
        const greyValue = rawValue(greyEntry);
        if (primaryValue == null || greyValue == null || greyValue <= 0) return null;

        return (primaryValue / greyValue) * 100;
    }, [showPerformance, tooltipProps.payload, theme.palette.primary.main, theme.palette.grey]);
    return (
        <Paper elevation={4} sx={{ p: 2 }}>
            <Box maxWidth="25em">
                <Typography variant="subtitle2" noWrap>
                    {type === FieldReportType.Overall && tooltipProps.label}
                    {type === FieldReportType.OverTime &&
                        (timeFrame === ReportDateTimeframe.Weekly ||
                            timeFrame === ReportDateTimeframe.BiWeekly ||
                            timeFrame === ReportDateTimeframe.Monthly ||
                            timeFrame === ReportDateTimeframe.Range ||
                            timeFrame === ReportDateTimeframe.All) &&
                        tooltipProps.label}
                    {type === FieldReportType.OverTime &&
                        timeFrame === ReportDateTimeframe.Daily &&
                        tooltipProps.label}
                </Typography>
            </Box>
            <Box>
                {tooltipProps.payload.map(p => {
                    const hms =
                        metric === PCAReportMetric.Time
                            ? secondsToHms(p.payload[p.dataKey])
                            : undefined;
                    const expectedHms =
                        metric === PCAReportMetric.Time && field != null
                            ? secondsToHms(p.payload[`${p.dataKey} ${expectedLabel}`])
                            : undefined;

                    const fullKey = `${p.dataKey}__full`;
                    const isPercentMode = p.payload?.[fullKey] !== undefined;
                    if (isPercentMode) {
                        const baseLabel = p.payload?.[`${p.dataKey}__baseLabel`] || expectedLabel;
                        return (
                            <Box key={p.dataKey} sx={{ color: p.color }}>
                                {!hideActualValue && (
                                    <Typography variant="body1">
                                        {p.dataKey}:{' '}
                                        <NumberFormatWhole value={p.payload[fullKey]} /> (
                                        <NumberFormat value={p.payload[p.dataKey]} />% of{' '}
                                        {baseLabel})
                                    </Typography>
                                )}
                            </Box>
                        );
                    }

                    return (
                        <Box
                            key={p.dataKey}
                            sx={{
                                color: p.color,
                            }}
                        >
                            {!hideActualValue && (
                                <Typography variant="body1">
                                    {p.dataKey}:{' '}
                                    {metric === PCAReportMetric.Time && hms != null ? (
                                        <>
                                            <Translate
                                                path="Reporting.ticks.hoursLabel"
                                                data={{ count: formatNumber(hms.h) }}
                                            />
                                            {', '}
                                            <Translate
                                                path="Reporting.ticks.minutesLabel"
                                                data={{ count: formatNumber(hms.m) }}
                                            />
                                            {hms.s > 0 && (
                                                <>
                                                    {', '}
                                                    <Translate
                                                        path="Reporting.ticks.secondsLabel"
                                                        data={{ count: formatNumber(hms.s) }}
                                                    />
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <NumberFormatWhole value={p.payload[p.dataKey]} />
                                    )}
                                    {type === FieldReportType.OverTime &&
                                        field != null &&
                                        field !== 'creative_name' &&
                                        !hidePercentage &&
                                        p.payload[`${p.dataKey} ${percentageLabel}`] != null && (
                                            <>
                                                /{' '}
                                                {metric === PCAReportMetric.Time &&
                                                expectedHms != null ? (
                                                    <>
                                                        <Translate
                                                            path="Reporting.ticks.hoursLabel"
                                                            data={{
                                                                count: formatNumber(expectedHms.h),
                                                            }}
                                                        />
                                                        {', '}
                                                        <Translate
                                                            path="Reporting.ticks.minutesLabel"
                                                            data={{
                                                                count: formatNumber(expectedHms.m),
                                                            }}
                                                        />
                                                        {expectedHms.s > 0 && (
                                                            <>
                                                                {', '}
                                                                <Translate
                                                                    path="Reporting.ticks.secondsLabel"
                                                                    data={{
                                                                        count: formatNumber(
                                                                            expectedHms.s,
                                                                        ),
                                                                    }}
                                                                />
                                                            </>
                                                        )}
                                                    </>
                                                ) : (
                                                    <NumberFormatWhole
                                                        value={
                                                            p.payload[
                                                                `${p.dataKey} ${expectedLabel}`
                                                            ]
                                                        }
                                                    />
                                                )}{' '}
                                                (
                                                <NumberFormat
                                                    value={
                                                        p.payload[`${p.dataKey} ${percentageLabel}`]
                                                    }
                                                />
                                                %)
                                            </>
                                        )}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
                {(() => {
                    const first = tooltipProps.payload?.[0];
                    if (!first || first.payload?.[`${first.dataKey}__full`] === undefined) {
                        return null;
                    }
                    const baseLabel = first.payload?.[`${first.dataKey}__baseLabel`];
                    const baseValue = first.payload?.[`${first.dataKey}__baseValue`];
                    if (!baseLabel || baseValue == null) return null;
                    return (
                        <Box>
                            <Typography variant="body1">
                                {baseLabel}: <NumberFormatWhole value={baseValue} />
                            </Typography>
                        </Box>
                    );
                })()}
                {(type === FieldReportType.Overall ||
                    (type === FieldReportType.OverTime && field == null)) &&
                    !hidePercentage &&
                    field !== 'creative_name' && (
                        <Box>
                            <Typography variant="body1">
                                {percentageLabel}:{' '}
                                <NumberFormat
                                    value={tooltipProps.payload?.[0]?.payload?.[percentageLabel]}
                                />
                                %
                            </Typography>
                        </Box>
                    )}
                {showPerformance && performance != null && (
                    <Box>
                        <Typography variant="body1">
                            {performanceLabel}: <NumberFormat value={performance} />%
                        </Typography>
                    </Box>
                )}
                {showCountFields && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        {countFields.indexOf(PCAReportCountField.InSchedule) !== -1 && (
                            <CountField
                                metric={metric}
                                count={tooltipProps.payload?.[0]?.payload?.[inScheduleLabel]}
                                label={inScheduleLabel}
                            />
                        )}
                        {countFields.indexOf(PCAReportCountField.OutSchedule) !== -1 && (
                            <CountField
                                metric={metric}
                                count={tooltipProps.payload?.[0]?.payload?.[outScheduleLabel]}
                                label={outScheduleLabel}
                            />
                        )}
                        {countFields.indexOf(PCAReportCountField.UnBooked) !== -1 && (
                            <CountField
                                metric={metric}
                                count={tooltipProps.payload?.[0]?.payload?.[unbookedLabel]}
                                label={unbookedLabel}
                            />
                        )}
                    </>
                )}
            </Box>
        </Paper>
    );
};

const CountField: React.FC<{ count: number; label: string; metric: PCAReportMetric }> = ({
    count,
    label,
    metric,
}) => (
    <Box>
        <Typography variant="body1">
            {label}:{' '}
            {metric === PCAReportMetric.Time ? (
                <HoursMins
                    variant="body1"
                    color={null}
                    seconds={count}
                    alwaysShowHours
                    alwaysShowMinutes
                    alwaysShowSeconds
                />
            ) : (
                formatNumberWhole(count)
            )}
        </Typography>
    </Box>
);
export default CustomTooltip;
